import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Headset, Building2, UserPlus, Bot, Upload } from "lucide-react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Tooltip,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Logout as LogoutIcon,
  Assignment as AssignmentIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Dashboard as DashboardIcon,
  Menu as MenuIcon,
} from "@mui/icons-material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { useAuth } from "../context/AuthContext";

const Header = () => {
  const { user, isLoggedIn, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:900px)");
  const isSuperAdmin =
    user && user.role && user.role.toLowerCase() === "sadmin";
  const isAdmin = user && user.role && user.role.toLowerCase() === "admin";

  if (location.pathname.startsWith("/chatbot")) {
    return null;
  }
  if (!isLoggedIn) {
    return (
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: "#1976d2",
          borderBottom: "1px solid rgba(255, 255, 255, 0.15)",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "flex-start",
            maxWidth: "1280px",
            width: "100%",
            margin: "0 auto",
            padding: { xs: "0.5rem 1rem", md: "0.5rem 2rem" },
            minHeight: { xs: "64px", sm: "70px" },
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              component="img"
              src="/ailaa_log.png"
              alt="Logo"
              sx={{
                height: { xs: "32px", sm: "40px" },
                width: "auto",
                borderRadius: "4px",
                mr: { xs: 1, sm: 2 },
              }}
              onClick={() => navigate("/")}
            />
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 600,
                display: { xs: "none", sm: "block" },
                letterSpacing: "0.5px",
                fontSize: { sm: "1.1rem", md: "1.25rem" },
              }}
            >
              Real Estate Management
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
    );
  }

  const handleLogout = () => {
    handleCloseMenu();
    logout();
    navigate("/signin");
  };

  const handleLead = () => {
    handleCloseMenu();
    closeMobileDrawer();
    navigate("/addlead");
  };

  const handleUploadcsv = () => {
    handleCloseMenu();
    closeMobileDrawer();
    navigate("/UploadCSV");
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleAddOrganization = () => {
    handleCloseMenu();
    closeMobileDrawer();
    navigate("/organizations");
  };
  const handleAddBot = () => {
    handleCloseMenu();
    closeMobileDrawer();
    navigate("/bots");
  };
  const handleAddCredit = () => {
    handleCloseMenu();
    closeMobileDrawer();
    navigate("/credits/new");
  };

  const handleWhatsapp = () => {
    handleCloseMenu();
    closeMobileDrawer();
    navigate("/whatsapp");
  };
  const handleAddUser = () => {
    handleCloseMenu();
    closeMobileDrawer();
    navigate("/users");
  };

  const handleAddProject = () => {
    handleCloseMenu();
    closeMobileDrawer();
    navigate("/projects");
  };

  const handleDashboard = () => {
    handleCloseMenu();
    closeMobileDrawer();
    navigate("/");
  };

  const toggleMobileDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const closeMobileDrawer = () => {
    setMobileDrawerOpen(false);
  };

  const showBackButton =
    location.pathname.startsWith("/clients/dashboard") ||
    location.pathname.startsWith("/organizations") ||
    location.pathname.startsWith("/projects") ||
    location.pathname.startsWith("/users") ||
    location.pathname.startsWith("/bots") ||
    location.pathname.startsWith("/credits/new") ||
    location.pathname.startsWith("/UploadCSV");

  const mobileDrawerContent = (
    <Box
      sx={{
        width: 250,
        height: "100%",
        backgroundColor: "#ffffff",
        display: "flex",
        flexDirection: "column",
      }}
      role="presentation"
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          backgroundColor: "#1976d2",
          color: "white",
        }}
      >
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: "rgba(255, 255, 255, 0.9)",
            color: "#1976d2",
            fontWeight: "bold",
            mr: 2,
          }}
        >
          {user?.full_name ? user.full_name.charAt(0) : "?"}
        </Avatar>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {user?.full_name || ""}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {user?.role || ""}
          </Typography>
        </Box>
      </Box>

      <List sx={{ flexGrow: 1, pt: 0 }}>
        <ListItem button onClick={handleDashboard} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <DashboardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItem>

        {isSuperAdmin && (
          <>
            <ListItem button onClick={handleAddOrganization} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <Building2 size={20} />
              </ListItemIcon>
              <ListItemText primary="Organization" />
            </ListItem>

            <ListItem button onClick={handleAddProject} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <AssignmentIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Project" />
            </ListItem>

            <ListItem button onClick={handleAddUser} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <UserPlus size={20} />
              </ListItemIcon>
              <ListItemText primary="User" />
            </ListItem>

            <ListItem button onClick={handleAddBot} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <Bot size={20} />
              </ListItemIcon>
              <ListItemText primary="Bot" />
            </ListItem>

            <ListItem button onClick={handleWhatsapp} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <CreditCardIcon size={20} />
              </ListItemIcon>
              <ListItemText primary="WhatsApp Setting" />
            </ListItem>
          </>
        )}

        {(isSuperAdmin || isAdmin) && (
          <>
            <ListItem button onClick={handleAddCredit} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <CreditCardIcon size={20} />
              </ListItemIcon>
              <ListItemText primary="Credit" />
            </ListItem>

            <ListItem button onClick={handleUploadcsv} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <CreditCardIcon size={20} />
              </ListItemIcon>
              <ListItemText primary="Upload" />
            </ListItem>
          </>
        )}
      </List>

      <Divider />

      <List>
        <ListItem
          button
          onClick={handleLogout}
          sx={{ py: 1.5, color: "#f44336" }}
        >
          <ListItemIcon sx={{ color: "#f44336" }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Sign Out" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: "#1976d2",
          borderBottom: "1px solid rgba(255, 255, 255, 0.15)",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            maxWidth: "1280px",
            width: "100%",
            margin: "0 auto",
            padding: { xs: "0.5rem 1rem", md: "0.5rem 2rem" },
            minHeight: { xs: "64px", sm: "70px" },
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            {isMobile && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={toggleMobileDrawer}
                sx={{
                  mr: 1,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {showBackButton && (
              <Tooltip title="Go back">
                <IconButton
                  onClick={() => navigate(-1)}
                  color="inherit"
                  edge="start"
                  sx={{
                    mr: 1,
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
            )}

            <Box
              component="img"
              src="/ailaa_log.png"
              alt="Logo"
              sx={{
                height: { xs: "32px", sm: "40px" },
                width: "auto",
                borderRadius: "4px",
                mr: { xs: 1, sm: 2 },
                cursor: "pointer",
              }}
              onClick={() => navigate("/")}
            />

            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 600,
                display: { xs: "none", sm: "block" },
                letterSpacing: "0.5px",
                fontSize: { sm: "1.1rem", md: "1.25rem" },
              }}
            >
              Real Estate Management
            </Typography>
          </Box>

          {!isMobile && (
            <Box display="flex" alignItems="center" gap={1}>
              {isTablet && (
                <Tooltip title="Add Lead">
                  <IconButton
                    color="inherit"
                    onClick={handleLead}
                    sx={{
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                  >
                    <Headset />
                  </IconButton>
                </Tooltip>
              )}

              <Button
                color="inherit"
                onClick={handleMenuClick}
                endIcon={<KeyboardArrowDownIcon />}
                startIcon={
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: "rgba(255, 255, 255, 0.9)",
                      color: "#1976d2",
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                    }}
                  >
                    {user?.full_name ? user.full_name.charAt(0) : "?"}
                  </Avatar>
                }
                sx={{
                  ml: 1,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <Box sx={{ display: { xs: "none", sm: "block" } }}>
                  {user?.role}
                </Box>
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    mt: 1,
                    minWidth: 220,
                    borderRadius: "8px",
                    py: 1,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Signed in as
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {user?.full_name || "User"}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <MenuItem onClick={handleDashboard} sx={{ py: 1.5 }}>
                  <ListItemIcon>
                    <DashboardIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Dashboard" />
                </MenuItem>

                {isSuperAdmin && (
                  <>
                    <MenuItem onClick={handleAddOrganization} sx={{ py: 1.5 }}>
                      <ListItemIcon>
                        <Building2 size={20} />
                      </ListItemIcon>
                      <ListItemText primary="Organization" />
                    </MenuItem>

                    <MenuItem onClick={handleAddProject} sx={{ py: 1.5 }}>
                      <ListItemIcon>
                        <AssignmentIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Project" />
                    </MenuItem>

                    <MenuItem onClick={handleAddUser} sx={{ py: 1.5 }}>
                      <ListItemIcon>
                        <UserPlus size={20} />
                      </ListItemIcon>
                      <ListItemText primary="User" />
                    </MenuItem>

                    <MenuItem onClick={handleAddBot} sx={{ py: 1.5 }}>
                      <ListItemIcon>
                        <Bot size={20} />
                      </ListItemIcon>
                      <ListItemText primary="Bot" />
                    </MenuItem>

                    <MenuItem onClick={handleWhatsapp} sx={{ py: 1.5 }}>
                      <ListItemIcon>
                        <CreditCardIcon size={20} />
                      </ListItemIcon>
                      <ListItemText primary="Whatsapp Setting" />
                    </MenuItem>
                  </>
                )}

                {(isSuperAdmin || isAdmin) && (
                  <>
                    <MenuItem onClick={handleAddCredit} sx={{ py: 1.5 }}>
                      <ListItemIcon>
                        <CreditCardIcon size={20} />
                      </ListItemIcon>
                      <ListItemText primary="Credit" />
                    </MenuItem>
                    <MenuItem onClick={handleUploadcsv} sx={{ py: 1.5 }}>
                      <ListItemIcon>
                        <Upload size={20} />
                      </ListItemIcon>
                      <ListItemText primary="Upload" />
                    </MenuItem>
                  </>
                )}

                <Divider sx={{ my: 1 }} />

                <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Sign Out"
                    sx={{ color: "error.main" }}
                  />
                </MenuItem>
              </Menu>
            </Box>
          )}

          <Drawer
            anchor="left"
            open={mobileDrawerOpen}
            onClose={closeMobileDrawer}
            sx={{
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: 250,
              },
            }}
          >
            {mobileDrawerContent}
          </Drawer>
        </Toolbar>
      </AppBar>
    </>
  );
};

export default Header;
